import { ReactNode } from 'react';
import { Checkbox, CHECKBOX_STATUS } from 'ui/checkbox';

import {
  CheckboxLabel,
  PaddedExpandableFormSectionContent,
  PaddedFormSectionContent,
  RowWrapper,
  StyledCaption,
} from 'modules/workflowNodeEditors/EditorBody.styled';
import { TipPlacement, Tooltip } from 'ui/tooltip';

type updateFunction = (isEnabled: boolean) => boolean;

export type ExpandableCheckboxProps = {
  caption?: ReactNode;
  children?: ReactNode;
  id: string;
  label: string;
  labelSupplement?: ReactNode;
  isChecked: boolean /* Whether the checkbox is checked */;
  onEnabledUpdate: (updateCallback: updateFunction) => void;
  isDisabled?: boolean /* Whether the checkbox is disabled for user interaction */;
  hasTooltip?: boolean;
  tooltipContent?: string;
  tooltipPlacement?: TipPlacement;
};

export const ExpandableCheckbox = ({
  caption,
  children,
  id,
  isChecked,
  isDisabled = false,
  onEnabledUpdate,
  label,
  labelSupplement,
  hasTooltip = false,
  tooltipContent,
  tooltipPlacement,
  ...rest
}: ExpandableCheckboxProps) => (
  <>
    <PaddedExpandableFormSectionContent
      data-test={`${id}-expandable-checkbox`}
      isExpanded={isChecked}
      {...rest}
    >
      <RowWrapper>
        <Tooltip
          isEnabled={hasTooltip}
          tooltipContent={tooltipContent}
          placement={tooltipPlacement}
        >
          <Checkbox
            data-test={`${id}-checkbox`}
            id={id}
            status={isChecked ? CHECKBOX_STATUS.CHECKED : CHECKBOX_STATUS.UNCHECKED}
            isDisabled={isDisabled}
            labelId={`${id}-label`}
            onClick={() => onEnabledUpdate((isOn) => !isOn)}
          />
        </Tooltip>
        <CheckboxLabel id={`${id}-label`} htmlFor={id}>
          {label}
        </CheckboxLabel>
        {!!labelSupplement && labelSupplement}
      </RowWrapper>
      {!!caption && <StyledCaption>{caption}</StyledCaption>}
    </PaddedExpandableFormSectionContent>
    {isChecked && !!children && (
      <PaddedFormSectionContent data-test={`${id}-details`}>{children}</PaddedFormSectionContent>
    )}
  </>
);
